import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { createSearchParams, Link } from 'react-router-dom';
import { format, parse } from 'date-fns';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  styled,
  TableHead,
} from '@mui/material';
import {
  activeCountriesSelector,
  dashboardSovosImportStatusesSelector,
  masterDataSelector,
} from '../../../../state';
import { useMuiPickersLocale } from '../../../../hooks';
import routes from '../../../../config/routes';
import { SovosImportStatus } from '../../../../types';
import StatusCell from '../../../StatusCell';

const CenteredTableCell = styled(TableCell)({
  textAlign: 'center',
});

const StatusRow = ({ importDate, country, totalTickets, status, amount }: SovosImportStatus) => {
  const { t } = useTranslation();
  const locale = useMuiPickersLocale();
  const params = {
    country,
    importDate,
  };
  const parsedImportDate = parse(importDate, 'yyyy-MM-dd', new Date());
  const to = {
    pathname: routes.protectedNestedRoutes.fiscalDetail.index.path,
    search: `?${createSearchParams(params)}`,
  };

  return (
    <TableRow
      component={Link}
      to={to}
      hover
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      <CenteredTableCell component="th" scope="row">
        {country ? t(`operativeStructure.countries.${country}`) : '-'}
      </CenteredTableCell>
      <CenteredTableCell>{format(parsedImportDate, 'P', { locale })}</CenteredTableCell>
      <CenteredTableCell>{totalTickets}</CenteredTableCell>
      <CenteredTableCell>{amount}</CenteredTableCell>
      <CenteredTableCell>
        <StatusCell status={status} tPath={`fiscal-detail.states.${status}`} />
      </CenteredTableCell>
    </TableRow>
  );
};

const IncorporationError = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" color="error" padding={1} spacing={1}>
      <ErrorIcon color="error" />
      <Typography variant="body1" color="error">
        {t('errors.generics.loadingData')}
      </Typography>
    </Stack>
  );
};

const FiscalContent = () => {
  const { t } = useTranslation();
  const {
    data,
    loading: resourceLoading,
    error,
  } = useSelector(dashboardSovosImportStatusesSelector);
  const masterDataResource = useSelector(masterDataSelector);
  const activeCountriesResource = useSelector(activeCountriesSelector);

  const loading = resourceLoading || masterDataResource.loading || activeCountriesResource.loading;

  return (
    <Box>
      <TableContainer sx={{ maxHeight: 250 }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-stickyHeader': {
              top: 0,
              zIndex: 2,
              backgroundColor: theme => theme.palette.background.paper,
            },
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                backgroundColor: theme => theme.palette.background.paper,
              },
            }}
          >
            <TableRow>
              <TableCell align="center">{t('dashboard.common.country')}</TableCell>
              <TableCell align="center">{t('dashboard.common.authorization-date')}</TableCell>
              <TableCell align="center">{t('dashboard.common.gcs')}</TableCell>
              <TableCell align="center">{t('dashboard.common.amount')}</TableCell>
              <TableCell align="center">{t('dashboard.common.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!error &&
              !loading &&
              data.map(i => (
                <StatusRow
                  key={i.importDate.toString()}
                  importDate={i.importDate}
                  country={i.country}
                  totalTickets={i.totalTickets}
                  amount={i.amount}
                  status={i.status}
                />
              ))}
            {loading && (
              <>
                <TableRow>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              </>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={4}>
                  <IncorporationError />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FiscalContent;
